import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguageSwitch } from '@/hooks/useLanguage';
import { useAuth } from '@/hooks/useAuth';
import { useCart } from '@/contexts/CartContext';
import { useProducts } from '@/contexts/ProductsContext';
import { useSiteImage } from '@/hooks/useSiteImage';
import { useNavLinks } from '@/hooks/useHeaderFooterLinks';
import { useWishlist } from '@/hooks/useWishlist';
import { textOf, useShopConfig, useShopMembers, type ShopKind } from '@/lib/shopConfig';
import './site-header-v2.css';

// New-design header (shown when useSiteDesign().isNew), built from the
// approved draft: logo, the admin-managed nav links in the draft's order with
// a mega menu on Products, then language / search / wishlist / account / cart.
// Phones get the draft's left drawer. Products, prices and images come from
// ProductsContext, so new products appear in the menu and search on their own.

const LANGS = [
  { code: 'en', short: 'EN', label: 'English' },
  { code: 'ko', short: 'KR', label: '한국어' },
  { code: 'bn', short: 'BN', label: 'বাংলা' },
] as const;

const SET_PATH = '/3-step-treatment';
const STEPS = ['OPEN', 'TREAT', 'LOCK'];
const STEP_IMG: Record<string, string> = { OPEN: '/v2/p/booster-hero.webp', TREAT: '/v2/p/serum-hero.webp', LOCK: '/v2/p/cream-hero.webp' };
// The draft's nav order; links the admin adds later follow these.
const NAV_ORDER = ['/products', '/3-step-treatment', '/brand-story', '/content', '/stores', '/where-to-buy'];

function Icon({ d }: { d: string }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      {d.split('|').map((p, i) => (p.startsWith('c:') ? <circle key={i} cx={p.split(',')[1]} cy={p.split(',')[2]} r={p.split(',')[3]} /> : <path key={i} d={p} />))}
    </svg>
  );
}
const I = {
  menu: 'M4 7h16M4 12h16M4 17h10',
  chev: 'M7 10l5 5 5-5',
  arrow: 'M5 12h14M13 6l6 6-6 6',
  search: 'c:,11,11,6.5|M20 20l-4.2-4.2',
  heart: 'M12 20s-7-4.4-7-10a4 4 0 0 1 7-2.6A4 4 0 0 1 19 10c0 5.6-7 10-7 10z',
  user: 'c:,12,8.5,3.8|M4.5 20c1.2-3.6 4-5.4 7.5-5.4s6.3 1.8 7.5 5.4',
  bag: 'M5 8h14l-1 12H6z|M9 8V6a3 3 0 0 1 6 0v2',
  x: 'M6 6l12 12M18 6L6 18',
  tel: 'M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L15 13l5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2',
};

export default function SiteHeader({ preview = false }: { preview?: boolean }) {
  const { t } = useTranslation();
  const location = useLocation();
  const { currentLang, switchLanguage } = useLanguageSwitch();
  const prefix = currentLang === 'ko' ? '/ko' : currentLang === 'bn' ? '/bn' : '';
  const { currentUser, isAuthenticated, logout } = useAuth();
  const { totalItems, openCart } = useCart();
  const { products, routineSet } = useProducts();
  const { items: wished } = useWishlist();
  // Category / concern lists from Admin > Website > Home Page > Shop categories & concerns.
  const shop = useShopConfig();
  const members = useShopMembers();
  const firstOf = (key: string, kind: ShopKind) => members.find((m) => m[kind].includes(key));
  const shopCats = shop.categories.filter((c) => !c.hidden && firstOf(c.key, 'categories'));
  const shopConcerns = shop.concerns.filter((c) => !c.hidden && firstOf(c.key, 'concerns'));
  const logoUrl = useSiteImage('nav.logo', 'https://public.readdy.ai/ai/img_res/f8e8e9d8-9748-4109-8994-aa9e580acb44.png');
  const rank = (href: string) => (NAV_ORDER.indexOf(href) + 1 || NAV_ORDER.length + 1) as number;
  const navLinks = useNavLinks()
    .map((l) => ({ id: l.id, label: l.label[currentLang] || l.label.en, href: l.href, isExternal: l.is_external }))
    .sort((a, b) => rank(a.href) - rank(b.href));

  const [scrolled, setScrolled] = useState(false);
  const [drawer, setDrawer] = useState(false);
  const [search, setSearch] = useState(false);
  const [query, setQuery] = useState('');
  const [langOpen, setLangOpen] = useState(false);
  const [accOpen, setAccOpen] = useState(false);
  const langRef = useRef<HTMLDivElement>(null);
  const accRef = useRef<HTMLDivElement>(null);
  const searchInput = useRef<HTMLInputElement>(null);
  const [bump, setBump] = useState(0);
  const lastCount = useRef(totalItems);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // The bag bounces when something is added.
  useEffect(() => {
    if (totalItems > lastCount.current) setBump((n) => n + 1);
    lastCount.current = totalItems;
  }, [totalItems]);

  // Close everything on navigation.
  useEffect(() => {
    setDrawer(false);
    setSearch(false);
    setAccOpen(false);
    setLangOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = drawer ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [drawer]);

  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) setLangOpen(false);
      if (accRef.current && !accRef.current.contains(e.target as Node)) setAccOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setSearch(false);
      setDrawer(false);
      setLangOpen(false);
      setAccOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, []);

  useEffect(() => {
    if (search) searchInput.current?.focus();
  }, [search]);

  const steps = products.filter((p) => STEPS.includes(p.stepKeyword)).sort((a, b) => a.step - b.step);
  const nameOf = (p: (typeof products)[number]) => p.designV2?.name || p.name;
  const imgOf = (p: (typeof products)[number]) => STEP_IMG[p.stepKeyword] || p.image;
  const setName = routineSet?.designV2?.name || routineSet?.name || '';

  // Everything searchable: the 3-Step Set plus each product.
  const catalog = useMemo(() => {
    const list = products.map((p) => ({
      key: p.id,
      name: p.designV2?.name || p.name,
      price: p.price,
      image: STEP_IMG[p.stepKeyword] || p.image,
      href: `${prefix}/products/${p.id}`,
      text: [p.name, p.designV2?.name, p.subtitle, p.stepLabel, p.description, ...p.skinTypes, ...p.keyIngredients.map((k) => k.name)].join(' ').toLowerCase(),
    }));
    if (routineSet) {
      list.unshift({
        key: 'set',
        name: routineSet.designV2?.name || routineSet.name,
        price: routineSet.price,
        image: routineSet.image,
        href: prefix + SET_PATH,
        text: `${routineSet.name} ${routineSet.designV2?.name ?? ''} set gift box routine ${routineSet.description}`.toLowerCase(),
      });
    }
    return list;
  }, [products, routineSet, prefix]);
  const q = query.trim().toLowerCase();
  const results = q ? catalog.filter((c) => c.text.includes(q)) : catalog.slice(0, 4);

  const isActive = (href: string) => {
    const full = prefix + href;
    return location.pathname === full || location.pathname.startsWith(full + '/');
  };
  const navLink = (l: (typeof navLinks)[number], className?: string, children?: React.ReactNode) =>
    l.isExternal ? (
      <a key={l.id} href={l.href} target="_blank" rel="noopener noreferrer" className={className}>
        {children ?? l.label}
      </a>
    ) : (
      <Link key={l.id} to={prefix + l.href} className={className} style={isActive(l.href) ? { color: 'var(--wine)' } : undefined}>
        {children ?? l.label}
      </Link>
    );

  return (
    <div className="v2h">
      <header className={`hdr${scrolled ? ' scrolled' : ''}`}>
        <div className="hmain">
          <div className="hwrap">
            <button className="burger hic" type="button" aria-label={t('nav.menu', 'Open menu')} aria-expanded={drawer} onClick={() => setDrawer(true)}>
              <Icon d={I.menu} />
            </button>
            <Link className="logo" to={prefix + '/'}>
              <img src={logoUrl} alt="Rx.K Seoul" />
            </Link>
            <nav className="nav" aria-label="Main">
              {navLinks.map((l) =>
                l.href === '/products' && !l.isExternal ? (
                  <div key={l.id} className="mm">
                    <Link className="mmt" to={prefix + l.href} aria-haspopup="true" style={isActive(l.href) ? { color: 'var(--wine)' } : undefined}>
                      {l.label} <Icon d={I.chev} />
                    </Link>
                    <div className="mega">
                      <div className="hwrap">
                        <div>
                          <h5>{t('v2h.category', 'Category')}</h5>
                          {shopCats.map((c) => (
                            <Link key={c.key} to={`${prefix}/?cat=${c.key}`}>
                              {textOf(c.label, currentLang)}
                            </Link>
                          ))}
                          <Link className="all" to={prefix + '/products'}>
                            {t('homeV2.shop.allProducts', 'All products')} <Icon d={I.arrow} />
                          </Link>
                        </div>
                        <div>
                          <h5>{t('v2h.concern', 'Skin concern')}</h5>
                          {shopConcerns.map((c) => (
                            <Link key={c.key} to={`${prefix}/?concern=${c.key}`}>
                              {textOf(c.long, currentLang) || textOf(c.label, currentLang)}
                            </Link>
                          ))}
                        </div>
                        {steps.length > 0 && (
                          <div className="msteps">
                            <h5>{t('v2h.ritual', 'The 3-step ritual')}</h5>
                            {steps.map((p) => (
                              <Link key={p.id} className="mst" to={`${prefix}/products/${p.id}`}>
                                <img src={imgOf(p)} alt="" />
                                <span>
                                  <small>{p.stepLabel.toUpperCase()}</small>
                                  {nameOf(p)}
                                </span>
                              </Link>
                            ))}
                          </div>
                        )}
                        {routineSet && (
                          <Link className="mpromo" to={prefix + SET_PATH}>
                            <img src="/v2/giftbox.webp" alt="" />
                            <span>
                              <small>{t('v2h.giftSet', 'GIFT SET')}</small>
                              <b>{routineSet.savings ? t('v2h.saveOnSet', 'Save {{amount}} on the 3-Step Set', { amount: routineSet.savings }) : setName}</b>
                              <em>
                                {t('v2h.shopNow', 'Shop now')} <Icon d={I.arrow} />
                              </em>
                            </span>
                          </Link>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  navLink(l)
                ),
              )}
            </nav>
            <div className="hicons">
              <div ref={langRef} className={`hlang${langOpen ? ' open' : ''}`}>
                <button type="button" className="hlb" aria-haspopup="true" aria-expanded={langOpen} onClick={() => setLangOpen((o) => !o)}>
                  {LANGS.find((l) => l.code === currentLang)?.short ?? 'EN'} <Icon d={I.chev} />
                </button>
                {langOpen && (
                  <div className="hlm">
                    {LANGS.map((l) => (
                      <a
                        key={l.code}
                        href="#"
                        className={currentLang === l.code ? 'on' : ''}
                        onClick={(e) => {
                          e.preventDefault();
                          switchLanguage(l.code);
                          setLangOpen(false);
                        }}
                      >
                        {l.label}
                      </a>
                    ))}
                  </div>
                )}
              </div>
              <button className="hic" type="button" aria-label={t('v2h.search', 'Search')} aria-expanded={search} onClick={() => setSearch((s) => !s)}>
                <Icon d={I.search} />
              </button>
              <Link className="hic hwish bag" to={prefix + '/wishlist'} aria-label={t('v2h.wishlist', 'Wishlist')}>
                <Icon d={I.heart} />
                {wished.length > 0 && <b>{wished.length > 9 ? '9+' : wished.length}</b>}
              </Link>
              <div ref={accRef} className="hacc" style={{ position: 'relative' }}>
                {isAuthenticated ? (
                  <>
                    <button className="hic" type="button" aria-label={t('v2h.account', 'Account')} aria-expanded={accOpen} onClick={() => setAccOpen((o) => !o)}>
                      <Icon d={I.user} />
                    </button>
                    {accOpen && (
                      <div className="hlm hacm">
                        <p>
                          <b>{currentUser?.name}</b>
                          <small>{currentUser?.email}</small>
                        </p>
                        <Link to={prefix + '/my/profile'}>{t('my.profile.title')}</Link>
                        <Link to={prefix + '/my/orders'}>{t('my.orders.title')}</Link>
                        <Link to={prefix + '/wishlist'}>{t('v2h.wishlist', 'Wishlist')}</Link>
                        <a
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            logout();
                          }}
                        >
                          {t('my.logout')}
                        </a>
                      </div>
                    )}
                  </>
                ) : (
                  <Link className="hic" to={prefix + '/login'} aria-label={t('v2h.account', 'Account')}>
                    <Icon d={I.user} />
                  </Link>
                )}
              </div>
              <a
                key={bump}
                className={`hic bag${bump ? ' bump' : ''}`}
                href="#"
                aria-label={t('nav.cart', 'Cart')}
                onClick={(e) => {
                  e.preventDefault();
                  openCart();
                }}
              >
                <Icon d={I.bag} />
                <b>{totalItems > 9 ? '9+' : totalItems}</b>
              </a>
            </div>
          </div>
        </div>

        {search && (
          <div className="hsearch">
            <div className="hwrap">
              <label className="hsf">
                <Icon d={I.search} />
                <input
                  ref={searchInput}
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={t('v2h.searchPlaceholder', 'Search products, ingredients or skin concerns')}
                  autoComplete="off"
                />
                <button type="button" className="hsx" aria-label={t('v2h.closeSearch', 'Close search')} onClick={() => setSearch(false)}>
                  <Icon d={I.x} />
                </button>
              </label>
              <p className="hsh">{q ? t('v2h.results', '{{count}} results', { count: results.length }) : t('v2h.popular', 'Popular products')}</p>
              <div className="hsr">
                {results.length ? (
                  results.map((r) => (
                    <Link key={r.key} to={r.href}>
                      <img src={r.image} alt="" />
                      <span>{r.name}</span>
                      <b>{r.price}</b>
                    </Link>
                  ))
                ) : (
                  <p>{t('v2h.noResults', 'No products found. Try “serum” or “dark spots”.')}</p>
                )}
              </div>
            </div>
          </div>
        )}

        {(search || drawer) && (
          <div
            className="hshade"
            onClick={() => {
              setSearch(false);
              setDrawer(false);
            }}
          />
        )}

        {drawer && (
          <aside className="drawer" aria-label={t('nav.menu', 'Menu')}>
            <div className="dtop">
              <img src={logoUrl} alt="Rx.K Seoul" />
              <button type="button" className="hic dclose" aria-label={t('v2h.closeMenu', 'Close menu')} onClick={() => setDrawer(false)}>
                <Icon d={I.x} />
              </button>
            </div>
            <p className="dh">{t('homeV2.shop.byCategory', 'Shop by category')}</p>
            <div className="dcats">
              {shopCats.map((c) => (
                <Link key={c.key} to={`${prefix}/?cat=${c.key}`}>
                  <img src={c.image || firstOf(c.key, 'categories')?.image} alt="" />
                  <span>{textOf(c.label, currentLang)}</span>
                </Link>
              ))}
            </div>
            <nav className="dnav">
              <Link to={prefix + '/products'}>
                {t('homeV2.shop.allProducts', 'All products')} <Icon d={I.arrow} />
              </Link>
              {navLinks
                .filter((l) => l.href !== '/products')
                .map((l) =>
                  navLink(
                    l,
                    undefined,
                    <>
                      {l.label} <Icon d={I.arrow} />
                    </>,
                  ),
                )}
              <Link to={prefix + '/verify'}>
                {t('v2h.verify', 'Verify your product')} <Icon d={I.arrow} />
              </Link>
              <Link to={prefix + '/wishlist'}>
                {t('v2h.wishlist', 'Wishlist')}
                {wished.length > 0 ? ` (${wished.length})` : ''} <Icon d={I.arrow} />
              </Link>
            </nav>
            <div className="dfoot">
              <div className="lang">
                {LANGS.map((l) => (
                  <a
                    key={l.code}
                    href="#"
                    className={currentLang === l.code ? 'on' : ''}
                    onClick={(e) => {
                      e.preventDefault();
                      switchLanguage(l.code);
                    }}
                  >
                    {l.short}
                  </a>
                ))}
              </div>
              {isAuthenticated ? (
                <>
                  <Link className="dsign" to={prefix + '/my/orders'}>
                    <Icon d={I.user} /> {t('my.orders.title')}
                  </Link>
                  <a
                    className="dsign"
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      logout();
                    }}
                  >
                    {t('my.logout')}
                  </a>
                </>
              ) : (
                <Link className="dsign" to={prefix + '/login'}>
                  <Icon d={I.user} /> {t('v2h.signIn', 'Sign in')}
                </Link>
              )}
              <a className="dtel" href="tel:09617661166">
                <Icon d={I.tel} /> 09617 66 11 66
              </a>
            </div>
          </aside>
        )}
      </header>
      {preview && <div className="v2h-preview">New design preview · staff only</div>}
    </div>
  );
}
